import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const SubmitOfferModal = ({ request, onSubmit, onClose, currentLanguage }) => {
  const [proposedFee, setProposedFee] = useState(request?.carrierFee?.toString() || '');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const translations = {
    en: {
      submitOffer: 'Submit Offer',
      proposedFee: 'Proposed Carrier Fee',
      message: 'Your Message',
      messagePlaceholder: 'Explain why you\'re the best choice for this delivery...',
      suggestedFee: 'Suggested Fee',
      submit: 'Submit Offer',
      cancel: 'Cancel',
      required: 'This field is required'
    },
    ko: {
      submitOffer: '제안 제출',
      proposedFee: '제안 운송 수수료',
      message: '메시지',
      messagePlaceholder: '이 배송에 가장 적합한 이유를 설명하세요...',
      suggestedFee: '제안 수수료',
      submit: '제안 제출',
      cancel: '취소',
      required: '필수 항목입니다'
    }
  };

  const t = translations?.[currentLanguage];

  const formatCurrency = (amount) => {
    return `${amount?.toLocaleString()}원`;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!proposedFee || !message) return;

    setLoading(true);
    try {
      await onSubmit({
        proposedFee: parseInt(proposedFee?.replace(/,/g, '')),
        message
      });
      onClose();
    } catch (error) {
      console.error('Submit offer error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFeeChange = (e) => {
    const value = e?.target?.value?.replace(/,/g, '');
    if (/^\d*$/?.test(value)) {
      setProposedFee(value);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-200 p-4">
      <div className="bg-card rounded-lg shadow-elevation-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-4 md:p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="Send" size={20} color="var(--color-primary)" />
              </div>
              <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground">
                {t?.submitOffer}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-muted transition-base"
            >
              <Icon name="X" size={20} />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-4 md:p-6 space-y-4">
          <div className="bg-muted/50 rounded-lg p-4 space-y-2">
            <h3 className="font-heading font-semibold text-foreground">{request?.itemName}</h3>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Icon name="MapPin" size={14} />
              <span>{request?.originCountry} → {request?.destinationCountry}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">{t?.suggestedFee}:</span>
              <span className="text-base font-semibold text-primary">
                {formatCurrency(request?.carrierFee)}
              </span>
            </div>
          </div>

          <div>
            <Input
              label={t?.proposedFee}
              type="text"
              value={proposedFee ? parseInt(proposedFee)?.toLocaleString() : ''}
              onChange={handleFeeChange}
              placeholder="0"
              required
            />
          </div>

          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              {t?.message} <span className="text-destructive">*</span>
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e?.target?.value)}
              placeholder={t?.messagePlaceholder}
              rows={4}
              required
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              fullWidth
              disabled={loading}
            >
              {t?.cancel}
            </Button>
            <Button
              type="submit"
              variant="default"
              iconName="Send"
              iconPosition="left"
              fullWidth
              loading={loading}
              disabled={!proposedFee || !message}
            >
              {t?.submit}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SubmitOfferModal;